import React, { useState, useCallback, useEffect } from "react"
import API from "../../utils/API"
import Sweetalert from "../../utils/Sweetalert"

const MAJOR_TYPES = [
  { value: "", label: "Tous" },
  { value: "event", label: "Événement" },
  { value: "operation", label: "Opération" },
]

const MINOR_TYPES_EVENT = [
  { value: "", label: "Tous" },
  { value: "device_reboot", label: "Redémarrage" },
  { value: "user_login", label: "Connexion utilisateur" },
  { value: "user_logout", label: "Déconnexion utilisateur" },
  { value: "client_connect_ok", label: "Connexion client" },
  { value: "exception_event", label: "Exception" },
]

const MINOR_TYPES_OPERATION = [
  { value: "", label: "Tous" },
  { value: "system_operation", label: "Système" },
  { value: "configuration_operation", label: "Configuration" },
  { value: "data_operation", label: "Données" },
  { value: "user_manager", label: "Gestion utilisateurs" },
  { value: "security_manager", label: "Sécurité" },
  { value: "video_operation", label: "Vidéo" },
  { value: "picture_operation", label: "Image" },
  { value: "alg_warehouse", label: "Algorithmes" },
  { value: "device_access_operation", label: "Accès périphérique" },
]

const MINOR_TYPE_LABELS = [...MINOR_TYPES_EVENT, ...MINOR_TYPES_OPERATION].reduce((acc, t) => {
  if (t.value) acc[t.value] = t.label
  return acc
}, {})

const MAJOR_TYPE_LABELS = { event: "Événement", operation: "Opération" }

const MINOR_TO_MAJOR = Object.fromEntries([
  ...MINOR_TYPES_EVENT.filter((t) => t.value).map((t) => [t.value, "event"]),
  ...MINOR_TYPES_OPERATION.filter((t) => t.value).map((t) => [t.value, "operation"]),
])

const PAGE_SIZE_OPTIONS = [20, 50, 100]

function formatLogTimestamp(ts) {
  if (!ts) return "-"
  const date = new Date(ts * 1000)
  return date.toLocaleString("fr-FR").replace(" ", " à ")
}

function toDatetimeLocalValue(date) {
  const pad = (n) => String(n).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const MAX_FETCH_SIZE = 10000

export default function DeviceLogsModal({ totemId, onClose }) {
  const [allLogs, setAllLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  const [filtersOpen, setFiltersOpen] = useState(false)
  const [majorType, setMajorType] = useState("")
  const [minorType, setMinorType] = useState("")
  const [startTime, setStartTime] = useState(() => {
    const d = new Date()
    d.setMonth(d.getMonth() - 1)
    return toDatetimeLocalValue(d)
  })
  const [stopTime, setStopTime] = useState(toDatetimeLocalValue(new Date()))
  const [logContent, setLogContent] = useState("")

  const minorTypeOptions = majorType === "event"
    ? MINOR_TYPES_EVENT
    : majorType === "operation"
      ? MINOR_TYPES_OPERATION
      : []

  const filteredLogs = React.useMemo(() => {
    if (!logContent) return allLogs
    const needle = logContent.toLowerCase()
    return allLogs.filter((l) =>
      (l.log_content || "").toLowerCase().includes(needle) ||
      (l.log_detail_content || "").toLowerCase().includes(needle)
    )
  }, [allLogs, logContent])

  const totalCount = filteredLogs.length
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const safePage = Math.min(page, totalPages)
  const pageStart = (safePage - 1) * pageSize
  const logs = filteredLogs.slice(pageStart, pageStart + pageSize)

  const fetchLogs = useCallback(() => {
    setLoading(true)
    setError(null)

    const query_params = {}
    if (majorType) query_params.major_type = majorType
    if (minorType) query_params.minor_type = minorType
    if (startTime) query_params.start_time = Math.floor(new Date(startTime).getTime() / 1000)
    if (stopTime) query_params.stop_time = Math.floor(new Date(stopTime).getTime() / 1000)

    API.POST(
      `/admin/totem/${totemId}/ai-targeting-module/logs`,
      { offset: 0, size: MAX_FETCH_SIZE, query_params },
      (response) => {
        setAllLogs(response.data?.logs_data || [])
        setPage(1)
        setLoading(false)
      },
      (errorMessage) => {
        setError(errorMessage)
        setLoading(false)
      },
    )
  }, [totemId, majorType, minorType, startTime, stopTime])

  useEffect(() => {
    fetchLogs()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    setPage(1)
    fetchLogs()
  }

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize)
    setPage(1)
  }

  const handlePrev = () => {
    setPage((p) => Math.max(1, p - 1))
  }

  const handleNext = () => {
    setPage((p) => Math.min(totalPages, p + 1))
  }

  const handleMajorTypeChange = (value) => {
    setMajorType(value)
    setMinorType("")
  }

  return (
    <>
      <div className="modal fade show" style={{ display: "block" }} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-xl" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-file-alt mr-2"></i>
                Logs du module IA
              </h5>
              <button type="button" className="close" onClick={onClose}>
                <span>&times;</span>
              </button>
            </div>

            <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              {/* Filters */}
              <div className="mb-3">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setFiltersOpen((prev) => !prev)}
                >
                  <i className={`fas fa-${filtersOpen ? "chevron-up" : "chevron-down"} mr-1`}></i>
                  Filtres
                </button>

                {filtersOpen && (
                  <div className="border rounded p-3 mt-2">
                    <div className="row">
                      <div className="col-md-6 form-group">
                        <label>Date début</label>
                        <input
                          type="datetime-local"
                          className="form-control form-control-sm"
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                        />
                      </div>
                      <div className="col-md-6 form-group">
                        <label>Date fin</label>
                        <input
                          type="datetime-local"
                          className="form-control form-control-sm"
                          value={stopTime}
                          onChange={(e) => setStopTime(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-4 form-group">
                        <label>Type principal</label>
                        <select
                          className="form-control form-control-sm"
                          value={majorType}
                          onChange={(e) => handleMajorTypeChange(e.target.value)}
                        >
                          {MAJOR_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4 form-group">
                        <label>Sous-type</label>
                        <select
                          className="form-control form-control-sm"
                          value={minorType}
                          onChange={(e) => setMinorType(e.target.value)}
                          disabled={!majorType}
                        >
                          {majorType
                            ? minorTypeOptions.map((t) => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))
                            : <option value="">Sélectionnez un type principal</option>
                          }
                        </select>
                      </div>
                      <div className="col-md-4 form-group">
                        <label>Contenu</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Rechercher dans le contenu..."
                          value={logContent}
                          onChange={(e) => setLogContent(e.target.value)}
                        />
                        <small className="form-text text-muted">
                          <i className="fas fa-info-circle mr-1"></i>
                          Recherche dans le contenu et le détail des logs.
                        </small>
                      </div>
                    </div>

                    <button className="btn btn-sm btn-info" onClick={handleSearch} disabled={loading}>
                      <i className="fas fa-search mr-1"></i> Rechercher
                    </button>
                  </div>
                )}
              </div>

              {/* Loading */}
              {loading && (
                <div className="text-center py-4">
                  <div className="spinner-border text-info" role="status">
                    <span className="sr-only">Chargement...</span>
                  </div>
                  <p className="mt-2 text-muted">Interrogation du module IA en cours...</p>
                </div>
              )}

              {/* Error */}
              {error && !loading && (
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle mr-1"></i>
                  {error}
                </div>
              )}

              {/* Results */}
              {!loading && !error && (
                <>
                  {logs.length > 0 ? (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">{totalCount} résultat(s)</small>
                        <div className="d-flex align-items-center">
                          <small className="text-muted mr-2">Par page :</small>
                          {PAGE_SIZE_OPTIONS.map((s) => (
                            <button
                              key={s}
                              className={`btn btn-xs ${s === pageSize ? "btn-info" : "btn-default"} mr-1`}
                              onClick={() => handlePageSizeChange(s)}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-sm table-bordered table-striped mb-0">
                          <thead>
                            <tr>
                              <th style={{ width: "140px" }}>Date</th>
                              <th style={{ width: "100px" }}>Type</th>
                              <th style={{ width: "130px" }}>Sous-type</th>
                              <th style={{ width: "110px" }}>Utilisateur</th>
                              <th style={{ width: "110px" }}>IP</th>
                              <th>Contenu</th>
                              <th style={{ width: "80px" }}>Détail</th>
                            </tr>
                          </thead>
                          <tbody>
                            {logs.map((log, idx) => {
                              const majorType = log.major_type || MINOR_TO_MAJOR[log.minor_type]
                              return (
                              <tr key={idx}>
                                <td style={{ fontSize: "0.8rem" }}>
                                  {formatLogTimestamp(log.log_datetime)}
                                </td>
                                <td>
                                  <span className={`badge badge-${majorType === "event" ? "warning" : "info"}`}>
                                    {MAJOR_TYPE_LABELS[majorType] || majorType || "-"}
                                  </span>
                                </td>
                                <td style={{ fontSize: "0.8rem" }}>
                                  {MINOR_TYPE_LABELS[log.minor_type] || log.minor_type || "-"}
                                </td>
                                <td style={{ fontSize: "0.8rem" }}>{log.user_name || "-"}</td>
                                <td style={{ fontSize: "0.8rem" }}>{log.log_ip || "-"}</td>
                                <td style={{ fontSize: "0.8rem" }}>
                                  {log.log_content ? (
                                    <span className="badge badge-light">{log.log_content}</span>
                                  ) : "-"}
                                </td>
                                <td className="text-center">
                                  {log.log_detail_content ? (
                                    <button
                                      className="btn btn-xs btn-default"
                                      onClick={() => Sweetalert.fireInfo("Détail du log", log.log_detail_content)}
                                      title="Voir le détail"
                                    >
                                      <i className="fas fa-eye"></i>
                                    </button>
                                  ) : "-"}
                                </td>
                              </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <button
                          className="btn btn-sm btn-default"
                          onClick={handlePrev}
                          disabled={safePage <= 1}
                        >
                          <i className="fas fa-chevron-left mr-1"></i> Précédent
                        </button>
                        <small className="text-muted">
                          Page {safePage} / {totalPages}
                        </small>
                        <button
                          className="btn btn-sm btn-default"
                          onClick={handleNext}
                          disabled={safePage >= totalPages}
                        >
                          Suivant <i className="fas fa-chevron-right ml-1"></i>
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle mr-1"></i>
                      Aucun log trouvé.
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-info"
                onClick={fetchLogs}
                disabled={loading}
              >
                <i className="fas fa-sync-alt mr-1"></i> Rafraîchir
              </button>
              <button type="button" className="btn btn-default" onClick={onClose}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  )
}
